import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import {
  Column,
  CustomAction,
  CustomTableAction,
  DataTableComponent,
  NimmstaDataSource,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  DataConnection,
  DataConnectionStatus,
} from '@nimmsta/ng-commons-lib/models';
import { StatusBadgePipe } from '@nimmsta/ng-commons-lib/pipes';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { DataConnectionFormComponent } from './dataconnection-form.component';
import { ActivateDeactivateAction } from './activateDeactivateAction';
import { DataConnectionTemplateService } from '../services/dataConnectionTemplate.service';
import { TranslatePipe } from '@ngx-translate/core';

_('dataconnection.title');

@Component({
  selector: 'app-dataconnection-templates',
  templateUrl: './dataconnectionTemplates.component.html',
  imports: [
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    TranslatePipe,
  ],
  providers: [StatusBadgePipe],
})
export class DataConnectionTemplatesComponent implements OnInit {
  private service = inject(DataConnectionTemplateService);
  private modalService = inject(AppDialogService);
  private statusBadge = inject(StatusBadgePipe);

  source!: NimmstaDataSource<DataConnection>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<DataConnection>;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  customActions: CustomAction<DataConnection>[] = [];

  customTableActions: CustomTableAction<DataConnection>[] = [];

  error?: HttpErrorResponse | undefined;

  get columns(): Column[] {
    return [
      new Column('name', 'dataconnection.name', { sortable: true }),
      new Column('description', 'dataconnection.description'),
      new Column('dataConnectionHandler', 'dataconnection.type', {
        sortable: true,
        translate: true,
      }),
      new Column('status', 'dataconnection.template.status', {
        pipe: this.statusBadge,
      }),
    ];
  }

  ngOnInit(): void {
    this.source = new NimmstaDataSource<DataConnection>(this.service);
    const approveDeclineAction = new ActivateDeactivateAction(
      'dataconnection.activate',
    );
    this.customActions.push(approveDeclineAction);
    approveDeclineAction.subscribe((id) =>
      this.activateDeactivateConnection(id),
    );
    const editAction = new CustomAction('edit');
    this.customActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new CustomAction('delete', false, true);
    this.customActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));

    const exportAction = new CustomAction('export');
    this.customActions.push(exportAction);
    exportAction.subscribe(this.export.bind(this));

    const importAction = new CustomTableAction('import', 'download_2');
    this.customTableActions.push(importAction);
    importAction.subscribe(this.import.bind(this));
  }

  new(): void {
    this.openModal({} as DataConnection);
  }

  show(id: string): void {
    this.edit(id);
  }

  edit(id: string): void {
    this.openModal(this.getSelectedObject(id)!);
  }

  openModal(value: DataConnection): void {
    const ref = this.modalService.open(DataConnectionFormComponent, {
      data: {
        value,
        isTemplate: true,
      },
      size: 'xl',
    });
    const sub = ref.closed.subscribe(() => {
      this.tableComponent.loadDataPage();
      sub.unsubscribe();
    });
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  activateDeactivateConnection(id: string): void {
    const dataConnection = this.getSelectedObject(id)!;
    dataConnection.configuration = JSON.stringify(dataConnection.configuration);
    if (dataConnection.status === DataConnectionStatus.ACTIVE) {
      dataConnection.status = DataConnectionStatus.INACTIVE;
    } else {
      dataConnection.status = DataConnectionStatus.ACTIVE;
    }
    this.service.update(dataConnection).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  getSelectedObject(id: string): DataConnection | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }

  export(id: string): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clone = { ...this.getSelectedObject(id)! } as any;
    clone.configuration = { ...clone.configuration };
    delete clone.id;
    delete clone.name;
    delete clone.identifier;
    delete clone.configuration.password;
    delete clone.configuration.token;
    const element = document.createElement('a');
    const fileType = 'text/json';
    const code = JSON.stringify(clone, null, 2);
    element.setAttribute(
      'href',
      `data:${fileType};charset=utf-8,${encodeURIComponent(code)}`,
    );
    element.setAttribute('download', 'template.json');
    const event = new MouseEvent('click');
    element.dispatchEvent(event);
    element.remove();
  }

  import(): void {
    this.fileInput?.nativeElement.click();
  }

  importFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.openModal(JSON.parse(reader.result as string));
      };
      reader.readAsText(file);
    }
    target.value = '';
  }
}
